import { useEffect, useState, useRef } from "react";
import { Plus, Upload, FileSpreadsheet, Trash2 as TrashIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

function DeptMapEditor() {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/fuel/dept-map").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const update = (i, key, val) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () => setRows((rs) => [{ name: "", department: "" }, ...rs]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    try {
      const entries = rows.filter((r) => (r.name || "").trim());
      const res = await api.put("/fuel/dept-map", { entries });
      toast.success(`Table enregistrée — ${res.data.count} conducteur(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="dept-map-editor">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="font-heading text-sm font-semibold text-[#001A3B]">Correspondance Conducteur → Département</p>
          <p className="mt-0.5 text-xs text-[#8695AB]">Utilisée pour remplir automatiquement la colonne « Département » de l'Excel comptable. Le nom du conducteur est rapproché de la facture (correspondance souple).</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="dept-map-add"><Plus className="mr-1.5 h-4 w-4" />Ajouter</Button>
          <Button size="sm" onClick={save} disabled={saving || !rows} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="dept-map-save">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </div>
      </div>
      {rows === null ? <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-9 rounded" />)}</div> : (
        <div className="max-h-[380px] overflow-y-auto rounded-lg border border-[#EDF0F5]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F5F7FA] text-left text-xs uppercase tracking-wide text-[#4A5568]">
              <tr><th className="px-3 py-2 font-semibold">Conducteur</th><th className="px-3 py-2 font-semibold">Département</th><th className="w-10 px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-[#EDF0F5]" data-testid="dept-map-row">
                  <td className="px-2 py-1.5"><Input value={r.name} onChange={(e) => update(i, "name", e.target.value)} className="h-8" placeholder="NOM CONDUCTEUR" data-testid="dept-map-name" /></td>
                  <td className="px-2 py-1.5"><Input value={r.department} onChange={(e) => update(i, "department", e.target.value)} className="h-8" placeholder="DÉPARTEMENT" data-testid="dept-map-dept" /></td>
                  <td className="px-2 py-1.5 text-center"><button onClick={() => removeRow(i)} className="rounded p-1.5 text-[#4A5568] hover:bg-red-50 hover:text-red-600" data-testid="dept-map-delete"><TrashIcon className="h-4 w-4" /></button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-xs text-[#8695AB]">Aucune correspondance. Ajoutez une ligne.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Convertisseur() {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [refFile, setRefFile] = useState(null);
  const inputRef = useRef(null);
  const refInputRef = useRef(null);

  const convert = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Veuillez déposer un fichier PDF."); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    if (refFile) fd.append("reference", refFile);
    try {
      const res = await api.post("/fuel/convert", fd, { responseType: "blob", headers: { "Content-Type": "multipart/form-data" } });
      let name = refFile ? "TOTAL.xlsx" : "conversion_comptable.xlsx";
      const cd = res.headers["content-disposition"];
      if (cd) { const m = cd.match(/filename="?([^"]+)"?/); if (m) name = m[1]; }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(refFile ? "Feuille ajoutée — classeur TOTAL.xlsx téléchargé" : "Conversion réussie — fichier Excel téléchargé");
    } catch (e) {
      toast.error("Conversion impossible. Vérifiez qu'il s'agit bien d'une facture TotalEnergies détaillée.");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div>
      <PageHeader title="TOTAL ENERGIE" subtitle="Facture carburant PDF → Excel comptable de réconciliation." />
      <p className="mb-4 text-sm text-[#4A5568]">Déposez la facture <b>TotalEnergies</b> (PDF détaillé « Votre Facture et son détail »). L'application lit les sous-totaux HT par conducteur et génère la feuille comptable au format habituel. <b>Ajoutez votre classeur TOTAL.xlsx</b> ci-dessous pour reprendre l'ordre des conducteurs, les sociétés et les coefficients, et récupérer le classeur avec la nouvelle feuille ajoutée à la suite.</p>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white p-4">
        <span className="text-sm font-medium text-[#001A3B]">Classeur de référence :</span>
        <input ref={refInputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => setRefFile(e.target.files[0] || null)} data-testid="converter-ref-input" />
        <Button variant="outline" size="sm" onClick={() => refInputRef.current?.click()} data-testid="converter-ref-btn"><Plus className="mr-1.5 h-4 w-4" />Choisir TOTAL.xlsx (optionnel)</Button>
        {refFile ? (
          <span className="inline-flex items-center gap-2 rounded-full bg-[#F4EFE3] px-3 py-1 text-xs font-medium text-[#8F7440]" data-testid="converter-ref-name">{refFile.name}<button onClick={() => { setRefFile(null); if (refInputRef.current) refInputRef.current.value = ""; }} className="text-[#8F7440] hover:text-[#001A3B]">✕</button></span>
        ) : <span className="text-xs text-[#8695AB]">Sans classeur : une feuille seule est générée.</span>}
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); convert(e.dataTransfer.files[0]); }}
        onClick={() => !busy && inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${drag ? "border-[#A5894E] bg-[#F4EFE3]" : "border-[#E2E8F0] bg-white hover:border-[#001A3B]/40"}`}
        data-testid="converter-dropzone"
      >
        <FileSpreadsheet className="mb-3 h-12 w-12 text-[#001A3B]" />
        <p className="font-heading text-base font-semibold text-[#18221F]">Glissez-déposez le PDF à convertir en Excel</p>
        <p className="mt-1 text-xs text-[#8695AB]">Format .pdf · sortie .xlsx (format comptable)</p>
        {busy && <p className="mt-3 flex items-center gap-2 text-sm text-[#001A3B]"><Upload className="h-4 w-4 animate-pulse" />Conversion en cours…</p>}
        <input ref={inputRef} type="file" accept=".pdf" className="hidden" onChange={(e) => convert(e.target.files[0])} data-testid="converter-file-input" />
      </div>
      <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F5F7FA] p-4 text-xs text-[#4A5568]">
        <span className="font-semibold text-[#001A3B]">Taux de TVA récupérable appliqués :</span> ESSENCE 80 % · SERVICE 0 % · PÉAGES 100 % · PARKING 100 % · FRAIS 100 %. Charge = HT + TVA non récupérable.
      </div>
      <DeptMapEditor />
    </div>
  );
}
